import { CollectionConfig } from "@rebasepro/types";
import { IS_REAL_USER } from "./_auth_predicates";
import companiesCollection from "./companies";

/**
 * Company Members — join table linking auth users to companies.
 *
 * One company can have several people (its "Equipo"): the first claims it at
 * registration, the rest join by invitation (`company_invitations`,
 * drizzle/0070). The schema would allow one person in two companies; the
 * acceptance refuses it, because the app shows one company per account.
 *
 * The `role` field is recorded and read by nothing — see `role` below.
 */
/**
 * A company may be claimed only while nobody holds a membership in it.
 *
 * That is exactly the self-serve registration case — RegisterCompanyForm
 * creates the company, then this row, as the same user seconds apart — while
 * making it impossible to attach yourself to a company someone else already
 * owns. Hoisted to a constant because the rule has to appear identically in
 * both clauses below, and two copies of a security predicate is one too many.
 *
 * The existence check goes through `public.company_has_members`, a
 * SECURITY DEFINER function, and must not be inlined back into a plain
 * `NOT EXISTS (SELECT 1 FROM company_members …)`. Written that way the subquery
 * is itself filtered by this table's own RLS, so it runs with the *caller's*
 * visibility — and `member_own_row` shows a caller only their own row. Someone
 * probing a company they are not in sees nothing, `NOT EXISTS` comes back true,
 * and the policy admits precisely the insert it exists to refuse. That version
 * was written, applied, and caught by the e2e guard still returning 201.
 */
/**
 * The `<> 'anonymous'` clause this used to carry did not hold: an
 * unauthenticated request carries `'anon'`, so a tokenless POST naming
 * `auth_user_id: "anon"` satisfied every conjunct and claimed ownership of any
 * company with no members — verified, HTTP 201. Since every anonymous caller
 * shares that uid, one such claim is a membership the whole internet holds.
 * `IS_REAL_USER` excludes both spellings; see `_auth_predicates.ts`.
 */
/**
 * And the caller must be the person who created that company, within the hour.
 *
 * "No members yet" was the whole test until drizzle/0062, and it described a
 * scraped company exactly as well as a self-registered one: ~170 records the
 * enrichment made to put a logo on a vacancy have no members, so any signed-in
 * account could POST one membership row and hold that employer's vacancies and
 * applicants. The intent written above was always narrower — the same user,
 * moments earlier — and now the rule says it.
 *
 * `company_claims` is where "who created it" lives: a trigger on `companies`
 * writes a `signup` row for the signed-in, non-admin caller of every insert
 * with `source = 'signup'` (the value RegisterCompanyForm sends). The caller
 * cannot write that row themselves — the table has no INSERT policy for them —
 * and `claimant_read_own` lets this subquery see their own rows and nobody
 * else's, which is exactly what a positive EXISTS needs.
 *
 * Every other way into a company goes through a backend function that checks
 * something first: `claim-company` (a confirmed address at the company's
 * domain, or a person's approval).
 */
const CREATED_BY_CALLER_MOMENTS_AGO =
    "EXISTS (SELECT 1 FROM company_claims cc " +
    "WHERE cc.company_id = company_members.company_id " +
    "AND cc.auth_user_id = auth.uid() " +
    "AND cc.method = 'signup' " +
    "AND cc.created_at > now() - interval '1 hour')";

const CLAIM_UNCLAIMED_COMPANY =
    "auth.uid() = auth_user_id " +
    `AND ${IS_REAL_USER} ` +
    "AND NOT public.company_has_members(company_id) " +
    `AND ${CREATED_BY_CALLER_MOMENTS_AGO}`;

const companyMembersCollection: CollectionConfig = {
    name: "Miembros de empresa",
    singularName: "Miembro de empresa",
    slug: "company-members",
    table: "company_members",
    securityRules: [
        {
            // Read and update your own membership row. Note this is NOT `all`:
            // INSERT is split out below, because the two need different
            // questions asked of them, and there is no DELETE at all — see
            // after `claim_unclaimed_company`.
            name: "member_own_row",
            operation: "select",
            using: "auth.uid() = auth_user_id"
        },
        {
            // Your own row, and only its contact fields in practice: a
            // BEFORE UPDATE trigger (drizzle/0070) refuses a change to
            // `company_id` or `auth_user_id` from anybody but an admin. This
            // policy alone never constrained the company the row points at, so
            // until 0070 one UPDATE on your own membership joined any company.
            name: "member_update_own_row",
            operation: "update",
            using: "auth.uid() = auth_user_id",
            withCheck: "auth.uid() = auth_user_id"
        },
        {
            // Co-members read each other — the "Equipo" list in "Mi empresa".
            //
            // Through `public.is_company_member`, a SECURITY DEFINER helper, and
            // never as `EXISTS (SELECT … FROM company_members …)`: a policy on
            // this table that subqueries this table is infinite recursion. The
            // helper answers one bit — is the caller a member of that company —
            // exactly as `company_has_members` does below. drizzle/0070.
            name: "member_read_team",
            operation: "select",
            using: "public.is_company_member(company_id)"
        },
        {
            // The membership *join* is the privilege boundary for this whole
            // schema, so its INSERT rule carries real weight.
            //
            // Checking only `auth.uid() = auth_user_id` — which is what an
            // `all` rule did — constrains who the row is *about* and says
            // nothing about which company it points at. Any signed-in user
            // could therefore insert a membership naming any existing company
            // and instantly gain that company's `company_read_own_jobs` and
            // `company_read_own_applications` access, plus — until drizzle/0060
            // scoped it to applicants — the whole candidate pool. Registering as a
            // talent and joining a real client company was two API calls.
            //
            // A company may only be claimed this way while it has no members
            // AND by the user who created it within the hour — the self-serve
            // registration case (RegisterCompanyForm.tsx), and since 0062
            // nothing wider. Taking over a company somebody else created (the
            // enrichment, mostly) is `functions/claim-company`, which checks
            // the address first. Once anyone holds a membership, the company
            // is closed to this path.
            //
            // Further members arrive by invitation (drizzle/0070), and the
            // invitation did not open this door: no rule here admits them. The
            // `company-invitation` function inserts the row on the owner path,
            // after checking the token, that the account's address is the
            // invited one and that it is verified.
            name: "claim_unclaimed_company",
            operation: "insert",
            // `using` is required by the type and ignored for INSERT — Postgres
            // has no USING clause there — so it mirrors `withCheck`, the same
            // shape `company_insert_pending` on jobs already uses.
            using: CLAIM_UNCLAIMED_COMPANY,
            withCheck: CLAIM_UNCLAIMED_COMPANY
        },
        // No DELETE rule for members, on purpose. `member_delete_own_row` let
        // the only member of a company delete their membership, which left the
        // company with none — and `claim_unclaimed_company` then admits the
        // next signed-in stranger, with the company's vacancies and applicants
        // behind it. Leaving and removing go through `company-team`, which
        // locks the company and refuses to remove its last member.
        //
        // And a RESTRICTIVE rule on top, because removing a rule from this file
        // does not remove its policy: Rebase re-creates declared policies on
        // every boot and drops only generated names, so an instance on an older
        // revision puts `member_delete_own_row` back. Restrictive is ANDed with
        // every permissive rule, so the old one grants nothing if it returns.
        // Admins and the trusted server context pass; the owner connection the
        // function uses is not bound by RLS, and a company deletion cascades
        // through the foreign key, which RLS does not check. drizzle/0070.
        // The same for joining. `claim_unclaimed_company` above is 0062's
        // tightened form; a backend from before 0062 re-creates the old one,
        // which never looked at company_claims and let any signed-in account
        // join any company nobody manages yet. This restrictive twin holds
        // whichever revision booted last. Invitations and approved claims are
        // written by the backend as admin, so they pass. drizzle/0075.
        {
            name: "only_claims_or_invitations_join_companies",
            operation: "insert",
            mode: "restrictive",
            using: `auth.uid() IS NULL OR string_to_array(auth.roles(), ',') && ARRAY['admin'] OR (${CLAIM_UNCLAIMED_COMPANY})`,
            withCheck: `auth.uid() IS NULL OR string_to_array(auth.roles(), ',') && ARRAY['admin'] OR (${CLAIM_UNCLAIMED_COMPANY})`
        },
        {
            name: "only_admins_delete_memberships",
            operation: "delete",
            mode: "restrictive",
            using: "auth.uid() IS NULL OR string_to_array(auth.roles(), ',') && ARRAY['admin']"
        },
        {
            name: "admin_full_access",
            operation: "all",
            roles: ["admin"]
        }
    ],
    properties: {
        id: {
            name: "ID",
            type: "string",
            isId: "uuid"
        },
        company_id: {
            name: "Empresa",
            type: "relation",
            relation: {
                kind: "belongsTo",
                target: () => companiesCollection,
                localKey: "company_id"
            },
            validation: { required: true },
            description: "La empresa a la que pertenece esta membresía"
        },
        auth_user_id: {
            name: "Usuario",
            type: "string",
            userSelect: true,
            validation: { required: true },
            description: "La persona que es miembro de la empresa"
        },
        role: {
            name: "Rol",
            type: "string",
            enum: [
                { id: "owner", label: "Propietario", color: "purple" },
                { id: "admin", label: "Administrador", color: "blue" },
                { id: "recruiter", label: "Reclutador", color: "teal" },
                { id: "viewer", label: "Solo lectura", color: "gray" },
                { id: "member", label: "Miembro", color: "gray" }
            ],
            // `member`, not `owner`: registration and acceptance both name their
            // role explicitly, so this only fills a membership an admin creates
            // in the panel — and "registró la empresa" would be untrue of that.
            defaultValue: "member",
            // What it says is what it does, which is nothing. No policy, function
            // or screen reads this column (checked 2026-09-21), so every member
            // can do everything any other can. The description used to promise
            // four levels of permission that were never built; an admin reading
            // it would have believed a "Solo lectura" member could not publish.
            // An invitation writes `member`; registration still writes `owner`,
            // which records who created the account and grants nothing extra.
            description: "Informativo: hoy no cambia lo que la persona puede hacer. Todos los miembros publican vacantes, ven las candidaturas e invitan o retiran a compañeros. «Propietario» es quien registró la empresa; «Miembro», quien entró por invitación."
        },
        contact_name: {
            name: "Nombre de contacto",
            type: "string",
            description: "Nombre visible de este miembro dentro de la empresa"
        },
        contact_position: {
            name: "Cargo",
            type: "string",
            description: "Cargo de la persona de contacto"
        },
        contact_email: {
            name: "Email de contacto",
            type: "string",
            description: "Email de trabajo de este miembro"
        },
        contact_phone: {
            name: "Teléfono de contacto",
            type: "string"
        },
        privacy_accepted: {
            name: "Privacidad aceptada",
            type: "boolean",
            validation: { required: true }
        },
        created_at: {
            name: "Se unió el",
            type: "date",
            autoValue: "on_create"
        }
    },
    admin: {
        sort: ["created_at", "desc"],
        // A join table between a company and a user. Nobody browses it on its
        // own — it belongs as the "Miembros" tab on a company, which is the only
        // context where a membership row means anything.
        //
        // Out of the drawer, but kept as the "Miembros" tab on a company —
        // which is the only context where a membership row means anything.
        //
        // `hideFromNavigation` governs the drawer alone. It used to strip the
        // parent's tab as well, which made exactly this configuration
        // impossible; `hideFromEntityViews` now covers that half separately.
        hideFromNavigation: true,
        group: "Empresas",
        icon: "Group",
        // Rendered as the "Miembros" tab on a company, so the company column
        // would repeat the row you are already inside.
        listProperties: ["contact_name", "contact_email", "role", "created_at"],
        previewProperties: ["contact_name", "role"]
    }
};

export default companyMembersCollection;
